import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Shield, Users, Award, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const About = () => {
  const values = [
    { icon: <Heart size={32} />, title: 'Compassion', desc: 'We believe every elderly person deserves care delivered with genuine warmth and empathy.' },
    { icon: <Shield size={32} />, title: 'Integrity', desc: 'Thorough screening, honest communication and ethical recruitment practices underpin everything we do.' },
    { icon: <Users size={32} />, title: 'Reliability', desc: 'Aged care homes trust PrimeShift because we deliver dependable staff, every time.' },
    { icon: <Award size={32} />, title: 'Quality', desc: 'We only place candidates who meet our high standards of professionalism and care.' },
  ];

  return (
    <div className="bg-white">
      {/* Header */}
      <section className="bg-care-blue text-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-care-green font-semibold text-sm mb-2 tracking-wide">ABOUT US</p>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">About PrimeShift Group</h1>
          <p className="text-xl text-white/80 max-w-3xl leading-relaxed">
            A Sydney-based recruitment agency specialising in permanent Personal Care Assistant placements for aged care homes and residential facilities.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-care-dark mb-6">Our Mission</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                PrimeShift Group's Aged Care Division was established with a clear purpose — to connect aged care facilities across Sydney with compassionate, qualified and reliable Personal Care Assistants for permanent positions.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                We understand that the quality of care in aged care homes depends entirely on the quality of staff. That's why we take recruitment seriously — screening every candidate thoroughly and matching them to facilities where they can make a genuine difference.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Our focus on permanent placements means we invest in finding the right long-term fit for both the facility and the carer, reducing turnover and building stable care teams.
              </p>
            </div>
            <div className="rounded-lg overflow-hidden h-80">
              <img
                src="https://images.pexels.com/photos/7551671/pexels-photo-7551671.jpeg?w=800&auto=compress"
                alt="Aged care professional"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-care-light">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-care-dark mb-12 text-center">Our Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((v, i) => (
              <Card key={i} className="border-0 shadow-sm bg-white">
                <CardContent className="p-6 text-center">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-care-light text-care-blue mb-4">
                    {v.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-care-dark mb-2">{v.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{v.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What we do */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-care-dark mb-8 text-center">What We Provide</h2>
          <div className="space-y-4">
            {[
              'Permanent PCA placements for aged care homes and residential facilities',
              'Thorough candidate screening — police clearances, NDIS checks, references',
              'Experienced staff trained in elderly care, personal hygiene and mobility support',
              'Fast response to staffing needs — quality candidates within days',
              'Ongoing support after placement to ensure satisfaction',
              'Coverage across the entire Sydney metropolitan area',
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4 p-4 rounded-lg bg-care-light">
                <CheckCircle className="w-5 h-5 text-care-green flex-shrink-0 mt-0.5" />
                <p className="text-gray-700">{item}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/clients">
              <Button className="bg-care-blue hover:bg-care-green text-white px-8 py-6 text-lg transition-colors duration-300">
                Request PCAs
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
